import { getLocalityFromAddress } from './addressUtils';
import { isLocationSearchAllProjectsFlow } from './isPremiumProject';

export function toSearchSlug(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Search location/city the user had when opening PDP from a location listing. */
export function getListingSearchContext(remainingData, routeState, searchParams) {
  const qs =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(String(searchParams || ''));
  const search = remainingData?.safeProps?.search || {};
  const location = (
    search.location ||
    routeState?.searchLocation ||
    routeState?.pdpSearchChipLocationLabel ||
    qs.get('location') ||
    ''
  )
    .toString()
    .trim();
  const city = (search.city || qs.get('city') || '').toString().trim();
  return { location, city };
}

export function isFromLocationSearchListing(remainingData, routeState) {
  if (isLocationSearchAllProjectsFlow(remainingData)) return true;
  if (routeState?.remainingData) return true;
  if (routeState?.pdpSearchChipLocationLabel) return true;
  if (routeState?.searchLocation) return true;
  return false;
}

/** Parse project payload into breadcrumb city + locality (never swap locality/city). */
export function resolveLocalityAndCityFromProject(projectData, title = '') {
  const proj = projectData?.project || projectData || {};
  const det = proj?.details || projectData?.details || {};
  const cityFromApi =
    proj?.city ||
    proj?.city_name ||
    det?.city ||
    projectData?.city ||
    projectData?.city_name ||
    projectData?.details?.city ||
    '';

  const trim = (s) => (typeof s === 'string' ? s.trim() : '');
  const locationCityStr = typeof cityFromApi === 'string' ? cityFromApi.trim() : '';
  const candidates = [
    det?.locality,
    proj?.locality,
    det?.area,
    proj?.area,
    proj?.project_location,
    det?.project_location,
    proj?.location,
    det?.location,
    projectData?.details?.locality,
    projectData?.details?.location,
    projectData?.location,
  ]
    .map(trim)
    .filter(Boolean);

  const fromAddress = (() => {
    const addr =
      proj?.geo_address ||
      det?.geo_address ||
      proj?.address ||
      det?.address ||
      projectData?.details?.address ||
      '';
    return getLocalityFromAddress(addr, locationCityStr);
  })();
  if (fromAddress) candidates.push(fromAddress);

  const titleLower = String(title || '').toLowerCase();
  const rawLocation = candidates.find((c) => c.toLowerCase() !== titleLower) || '';
  const locationStr = typeof rawLocation === 'string' ? rawLocation.trim() : '';
  const parts = locationStr ? locationStr.split(',').map((s) => s.trim()).filter(Boolean) : [];

  let cityFromProject = locationCityStr || null;
  let locationFromProject = null;

  if (parts.length >= 2) {
    locationFromProject = parts[0];
    if (!cityFromProject) cityFromProject = parts[parts.length - 1];
  } else if (parts.length === 1) {
    locationFromProject = parts[0];
  }

  return {
    cityFromProject: cityFromProject || null,
    locationFromProject: locationFromProject || null,
  };
}

/**
 * When user opened PDP from location search, keep listing search params on the URL.
 * Returns next search string or null when no change is needed.
 */
export function buildListingSearchQuerySync(currentSearch, listingContext) {
  const currentQs = new URLSearchParams(String(currentSearch || '').replace(/^\?/, ''));
  const listingLocation = listingContext?.location?.trim() || '';
  const listingCity = listingContext?.city?.trim() || '';
  if (!listingLocation && !listingCity) return null;

  const nextQs = new URLSearchParams(currentQs);
  if (listingLocation) nextQs.set('location', listingLocation);
  else nextQs.delete('location');
  if (listingCity) nextQs.set('city', listingCity);
  else nextQs.delete('city');

  const currentLoc = (currentQs.get('location') || '').trim();
  const currentCity = (currentQs.get('city') || '').trim();
  const locMatches =
    !listingLocation ||
    toSearchSlug(currentLoc) === toSearchSlug(listingLocation) ||
    currentLoc.toLowerCase() === listingLocation.toLowerCase();
  const cityMatches =
    !listingCity ||
    toSearchSlug(currentCity) === toSearchSlug(listingCity) ||
    currentCity.toLowerCase() === listingCity.toLowerCase();

  if (locMatches && cityMatches) return null;

  const nextSearch = nextQs.toString();
  return nextSearch ? `?${nextSearch}` : '';
}
